import * as React from 'react';
import { ProfileManager } from '../profile';

export interface APIRequest {
  data: any | null;
  complete: boolean;
  loading: boolean;
  error: Error | null;
}

const BASE_URL = process.env.FILTRU_API_URL;

const errorResponseStatuses: number[] = [400, 401, 404, 409, 500];

const initialState: APIRequest = {
  data: null,
  complete: false,
  loading: false,
  error: null,
};

const apiURL = (path: string): string => {
  return `${BASE_URL}${path}`;
};

const request = async (url: string, opts: any) => {
  const response = await fetch(apiURL(url), opts);
  const body = await response.json();

  if (response.ok) {
    return body;
  }

  if (errorResponseStatuses.includes(response.status)) {
    throw new Error(body.message || 'Invalid request');
  }

  if (response.status === 401) {
    throw new Error('Unauthorised');
  }

  throw new Error(response.statusText);
};

const loadingState = (): APIRequest =>
  Object.assign({}, initialState, { loading: true });
const completedState = (data: any): APIRequest =>
  Object.assign({}, initialState, { data, complete: true });
const errorState = (error: Error): APIRequest =>
  Object.assign({}, initialState, { error, complete: true });

const performRequest = async (req: any, setRes: Function) => {
  setRes(loadingState());

  try {
    const data = await request(req.url, {
      ...req,
      headers: {
        'Content-Type': 'application/json',
        ...req.headers,
      },
    });

    setRes(completedState(data));
  } catch (error) {
    setRes(errorState(error));
  }
};

export function getHeaders(context: ProfileManager): any {
  if (!context.profile) {
    return {};
  }

  return {
    'X-Token': context.profile.token,
    'X-User-ID': context.profile.id,
  };
}

/**
 * Performs an API request once a trigger function is called
 *
 * Usage example:
 *
 * const [state, submit] = useRequestAsync((formData) => {
 *    url: '/login',
 *    body: JSON.stringify(formData)
 * });
 *
 * ...
 *
 * submit({ name: …, password: … });
 *
 * @param fn
 */
export function useRequestAsync(fn: Function, scrollToTop: Boolean = true): [APIRequest, Function] {
  const [res, setRes] = React.useState<APIRequest>(initialState);
  const [req, setReq] = React.useState(null);

  React.useEffect(() => {
    if (scrollToTop) {
      // Scroll to top…
      window.scrollTo(0, 0);
    }

    (async () => {
      if (!req) return;
      await performRequest(req, setRes);
    })();
  }, [req]);

  return [res, (...args: any) => setReq(fn(...args))];
}

/**
 * Performs an API request immediately
 *
 * Usage example:
 *
 * const [state, nextPage] = useRequest((args) => {
 *    url: '/posts?page=',
 *    method: 'GET',
 * });
 *
 * @param fn
 */
export function useRequest(fn: Function): [APIRequest, Function] {
  const [res, setRes] = React.useState(initialState);
  const [req, setReq] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      const req = fn();
      await performRequest(req, setRes);
    })();
  }, [req]);

  return [res, setReq];
}
